import * as React from 'react'
import { getGroupedTimesheet } from '@/lib/api/company/timesheet/groupedTimesheet/getGroupedTimesheet'
import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'

const INCREMENTAL_DEBOUNCE_MS = 300

type Params = {
  companyId: string
  year: number
  month: number 
  sideCheckedIds: number[]
  domesticBusinessTripDateType?: 'start' | 'end'
  showBonusConfiguration?: boolean
}


export function useGroupedPayroll({
  companyId,
  year,
  month,
  sideCheckedIds,
  domesticBusinessTripDateType,
  showBonusConfiguration = true,
}: Params) {
  const [data, setData] = React.useState<PayrollDriverRow[]>([])
  const [isReady, setIsReady] = React.useState(false)
  const [isFetching, setIsFetching] = React.useState(false)
  const [error, setError] = React.useState<unknown>(null)

  const fetchedIdsRef = React.useRef<Set<number>>(new Set())
  const debounceRef = React.useRef<any>(null)
  const inFlightRef = React.useRef(0)
  const sideCheckedIdsRef = React.useRef<number[]>(sideCheckedIds)
  sideCheckedIdsRef.current = sideCheckedIds


  const paramsRef = React.useRef({
    companyId,
    year,
    month,
    domesticBusinessTripDateType,
    showBonusConfiguration,
  })
  paramsRef.current = {
    companyId,
    year,
    month,
    domesticBusinessTripDateType,
    showBonusConfiguration,
  }

  const fetchForIds = React.useCallback(
    async (ids: number[], mode: 'replace' | 'merge') => {
      const p = paramsRef.current

  
      if (mode === 'replace') {
        fetchedIdsRef.current = new Set(ids)
      } else {
        ids.forEach((id) => fetchedIdsRef.current.add(id))
      }

      if (ids.length === 0) {
        if (mode === 'replace') {
          setData([])
          setError(null)
          setIsReady(true)
        }
        return
      }

      inFlightRef.current += 1
      setIsFetching(true)
      setError(null)
      try {
        const { data: rows } = await getGroupedTimesheet({
          companyId: p.companyId,
          driversIds: ids,
          year: p.year,
          month: p.month,
          showBonusConfiguration: p.showBonusConfiguration,
          domesticBusinessTripDateType: p.domesticBusinessTripDateType,
        })
        setData((prev) => {
          if (mode === 'replace') return rows ?? []
          const map = new Map(prev.map((r) => [r.driver.id, r]))
          ;(rows ?? []).forEach((r) => map.set(r.driver.id, r))
          return Array.from(map.values())
        })
      } catch (err) {
        console.error('Error fetching grouped payroll:', err)
        setError(err)
        if (mode === 'replace') {
          setData([])
        } else {
          ids.forEach((id) => fetchedIdsRef.current.delete(id))
        }
      } finally {
        inFlightRef.current -= 1
        if (inFlightRef.current === 0) setIsFetching(false)
        setIsReady(true)
      }
    },
    [],
  )

  const fetchForIdsRef = React.useRef(fetchForIds)
  fetchForIdsRef.current = fetchForIds

  const refetch = React.useCallback(() => {
    return fetchForIdsRef.current(sideCheckedIdsRef.current, 'replace')
  }, [])


  React.useEffect(() => {
    fetchForIdsRef.current(sideCheckedIdsRef.current, 'replace')
  }, [companyId, year, month, domesticBusinessTripDateType, showBonusConfiguration])

  React.useEffect(() => {
    const newIds = sideCheckedIds.filter((id) => !fetchedIdsRef.current.has(id))

    if (newIds.length === 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
        if (inFlightRef.current === 0) setIsFetching(false)
      }
      return
    }

    setIsFetching(true)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      const ids = sideCheckedIdsRef.current.filter(
        (id) => !fetchedIdsRef.current.has(id),
      )
      if (ids.length === 0) {
        if (inFlightRef.current === 0) setIsFetching(false)
        return
      }
      fetchForIdsRef.current(ids, 'merge')
    }, INCREMENTAL_DEBOUNCE_MS)
  }, [sideCheckedIds])

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Same merge policy as the incremental fetch above (overwrite by driver.id) —
  // for callers that already hold fresh rows (e.g. the calculate flow's final
  // read) and want to apply them without a second identical request.
  const mergeRows = React.useCallback((rows: PayrollDriverRow[]) => {
    setData((prev) => {
      const map = new Map(prev.map((r) => [r.driver.id, r]))
      rows.forEach((r) => map.set(r.driver.id, r))
      return Array.from(map.values())
    })
  }, [])

  return {
    data,
    mergeRows,
    isReady,
    isFetching,
    isError: error != null,
    error,
    refetch,
  }
}
